import { useState, useEffect, useMemo } from 'react';
import { Head } from '@inertiajs/react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Search, Eye, Lock, Unlock } from 'lucide-react';
import { PlatformConsoleBreadcrumb } from '@/components/platform_console/PlatformConsoleBreadcrumb';
import { TopicDrawer } from '@/components/admin/TopicDrawer';
import { getTopics, getTopicsMetrics, type Topic, type TopicsParams } from '@/api/topics';
import { toast } from 'sonner';

export default function TopicsPage() {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metrics, setMetrics] = useState<{ total_topics: number; locked_count: number; median_size: number; p95_size: number } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [lockedFilter, setLockedFilter] = useState<'any' | 'true' | 'false'>('any');
  const [sortBy, setSortBy] = useState<'size' | 'updated_at' | 'avg_distance'>('size');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [perPage] = useState(25);
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchTopics = async (params: TopicsParams = {}) => {
    try {
      setLoading(true);
      const response = await getTopics({
        q: searchQuery || undefined,
        locked: lockedFilter !== 'any' ? lockedFilter : undefined,
        sort: sortBy,
        order: sortOrder,
        page: currentPage,
        per_page: perPage,
        ...params
      });

      setTopics(response.topics);
      setTotalPages(response.meta.total_pages);
      setTotalCount(response.meta.total_count);
    } catch (error) {
      console.error('Failed to fetch topics:', error);
      toast.error('Failed to load topics');
    } finally {
      setLoading(false);
    }
  };

  const fetchMetrics = async () => {
    try {
      setMetricsLoading(true);
      const response = await getTopicsMetrics();
      setMetrics(response);
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
      toast.error('Failed to load metrics');
    } finally {
      setMetricsLoading(false);
    }
  };

  useEffect(() => {
    // Avoid using cached responses for freshness
    const controller = new AbortController();
    void fetchTopics();
    return () => controller.abort();
  }, [searchQuery, lockedFilter, sortBy, sortOrder, currentPage]);

  useEffect(() => {
    fetchMetrics();
  }, []);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchTopics();
  };

  const handleSortChange = (newSort: 'size' | 'updated_at' | 'avg_distance') => {
    if (sortBy === newSort) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSort);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const handleTopicClick = (topic: Topic) => {
    setSelectedTopic(topic);
    setDrawerOpen(true);
  };

  const handleTopicUpdate = (updatedTopic: Topic) => {
    setTopics(prev => prev.map(t => t.id === updatedTopic.id ? updatedTopic : t));
    if (selectedTopic?.id === updatedTopic.id) {
      setSelectedTopic(updatedTopic);
    }
  };

  const maxSizeOnPage = useMemo(() => {
    return Math.max(...topics.map(t => t.size), 1);
  }, [topics]);

  const getSizeBarWidth = (size: number) => {
    return Math.min(100, (size / maxSizeOnPage) * 100);
  };

  const getLockedBadge = (locked: boolean) => {
    return (
      <Badge variant={locked ? "destructive" : "secondary"} className="flex items-center gap-1">
        {locked ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
        {locked ? 'Locked' : 'Unlocked'}
      </Badge>
    );
  };

  const formatDistance = (distance: number) => {
    return distance.toFixed(3);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <Head title="Topics" />

      <div className="container mx-auto p-6 max-w-7xl">
        <div className="space-y-6">
          <PlatformConsoleBreadcrumb section="internal_tools" current="Topics" />
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold">Topics</h1>
            <Badge variant="outline" className="ml-2">
              {totalCount} total
            </Badge>
          </div>

          {/* Metrics Toolbar */}
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary">
              {metricsLoading ? 'Loading…' : `Topics: ${metrics?.total_topics ?? 0}`}
            </Badge>
            <Badge variant="secondary">
              {metricsLoading ? 'Loading…' : `Locked: ${metrics?.locked_count ?? 0}`}
            </Badge>
            <Badge variant="secondary">
              {metricsLoading ? 'Loading…' : `Median size: ${metrics?.median_size ?? 0}`}
            </Badge>
            <Badge variant="secondary">
              {metricsLoading ? 'Loading…' : `p95 size: ${metrics?.p95_size ?? 0}`}
            </Badge>
          </div>

          {/* Search and Filter Bar */}
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div className="flex flex-col sm:flex-row gap-4 items-center flex-1">
                  <div className="flex gap-2 flex-1 max-w-md">
                    <Input
                      placeholder="Search topics..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      className="flex-1"
                    />
                    <Button onClick={handleSearch} variant="outline">
                      <Search className="h-4 w-4" />
                    </Button>
                  </div>

                  <Select value={lockedFilter} onValueChange={(value: 'any' | 'true' | 'false') => setLockedFilter(value)}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Locked" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="true">Locked</SelectItem>
                      <SelectItem value="false">Unlocked</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={`${sortBy}-${sortOrder}`} onValueChange={(value) => {
                    const [sort, order] = value.split('-') as [typeof sortBy, typeof sortOrder];
                    setSortBy(sort);
                    setSortOrder(order);
                    setCurrentPage(1);
                  }}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="size-desc">Size (High to Low)</SelectItem>
                      <SelectItem value="size-asc">Size (Low to High)</SelectItem>
                      <SelectItem value="updated_at-desc">Recently Updated</SelectItem>
                      <SelectItem value="updated_at-asc">Oldest Updated</SelectItem>
                      <SelectItem value="avg_distance-desc">Avg Distance (High)</SelectItem>
                      <SelectItem value="avg_distance-asc">Avg Distance (Low)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Topics Table */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="text-center py-12">
                  <div className="text-muted-foreground">Loading topics...</div>
                </div>
              ) : topics.length > 0 ? (
                <>
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-blue-500 hover:bg-blue-500">
                          <TableHead className="text-white font-semibold">Name</TableHead>
                          <TableHead className="text-white font-semibold">Size</TableHead>
                          <TableHead className="text-white font-semibold">Locked</TableHead>
                          <TableHead className="text-white font-semibold">Avg Distance</TableHead>
                          <TableHead className="text-white font-semibold">P95 Distance</TableHead>
                          <TableHead className="text-white font-semibold">Updated At</TableHead>
                          <TableHead className="text-white font-semibold">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {topics.map((topic) => (
                          <TableRow key={topic.id} className="cursor-pointer hover:bg-muted/50">
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <span className="truncate max-w-[200px]" title={topic.name}>
                                  {topic.name}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium min-w-[3rem]">
                                  {topic.size}
                                </span>
                                <div className="flex-1 min-w-[60px]">
                                  <Progress
                                    value={getSizeBarWidth(topic.size)}
                                    className="h-2"
                                  />
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              {getLockedBadge(topic.locked)}
                            </TableCell>
                            <TableCell className="text-sm font-mono">
                              {formatDistance(topic.avg_distance)}
                            </TableCell>
                            <TableCell className="text-sm font-mono">
                              {formatDistance(topic.p95_distance)}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatDate(topic.updated_at)}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleTopicClick(topic)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile Card Layout */}
                  <div className="md:hidden space-y-4 p-4">
                    {topics.map((topic) => (
                      <Card key={topic.id} className="cursor-pointer hover:bg-muted/50" onClick={() => handleTopicClick(topic)}>
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            <div>
                              <div className="font-semibold text-sm text-blue-600 mb-1">Name</div>
                              <div className="font-medium">{topic.name}</div>
                            </div>

                            <div>
                              <div className="font-semibold text-sm text-blue-600 mb-1">Size</div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">{topic.size}</span>
                                <Progress value={getSizeBarWidth(topic.size)} className="h-2 flex-1" />
                              </div>
                            </div>

                            <div>
                              <div className="font-semibold text-sm text-blue-600 mb-1">Status</div>
                              {getLockedBadge(topic.locked)}
                            </div>

                            <div>
                              <div className="font-semibold text-sm text-blue-600 mb-1">Distances</div>
                              <div className="text-sm font-mono">
                                Avg: {formatDistance(topic.avg_distance)} | P95: {formatDistance(topic.p95_distance)}
                              </div>
                            </div>

                            <div>
                              <div className="font-semibold text-sm text-blue-600 mb-1">Updated</div>
                              <div className="text-sm text-muted-foreground">
                                {formatDate(topic.updated_at)}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <div className="text-muted-foreground mb-4">
                    No topics found.
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Topic Drawer */}
      <TopicDrawer
        topic={selectedTopic}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onTopicUpdate={handleTopicUpdate}
        onMerged={(removedId?: number) => {
          setDrawerOpen(false);
          setSelectedTopic(null);
          if (removedId) {
            setTopics(prev => prev.filter(t => t.id !== removedId));
          }
          fetchTopics({ page: 1 });
          fetchMetrics();
          toast.success('Topics updated');
        }}
      />
    </>
  );
}
